import React, { useContext } from 'react';
import { Card, Checkbox, Col, Divider, Form, Row, Select, Typography, Button, Spin, message } from 'antd';
import Layout from '../engine/layout';
import { AppContext } from '../App';

const { Title, Text } = Typography;
const { Option } = Select;

const SettingsWrapper = (props) => {
    const app = useContext(AppContext);

    return <Settings app={app} {...props} />;
};

class Settings extends Layout {
    constructor(props) {
        super(props);
        this.app = props.app;

        const defaults = this.getDefaultsFromApp();

        this.state = {
            loading: true,
            saving: false,
            cardStyles: [
                { value: 'design-1', label: 'Design 1' },
                { value: 'design-2', label: 'Design 2' },
                { value: 'design-3', label: 'Design 3' },
            ],
            tableStyles: [
                { value: 'design-1', label: 'Design 1' },
                { value: 'design-2', label: 'Design 2' },
                { value: 'design-3', label: 'Design 3' },
            ],
            roomStyles: [
                { value: 'design-1', label: 'Design 1' },
                { value: 'design-2', label: 'Design 2' },
                { value: 'design-3', label: 'Design 3' },
            ],
            soundSignals: [
                { value: 'signal-1', label: 'Signāls 1' },
                { value: 'signal-2', label: 'Signāls 2' },
                { value: 'signal-3', label: 'Signāls 3' },
            ],
            values: defaults,
            formKey: 0,
        };
    }

    componentDidMount() {
        this.app = this.props.app;
        this.loadData();
    }

    componentDidUpdate() {
        this.app = this.props.app;
    }

    getDefaultsFromApp = () => {
        const settings = this.props.app?.state?.user?.settings ?? {};
        return {
            card_back: settings.card_back ?? 'design-1',
            table_design: settings.table_design ?? 'design-1',
            room_design: settings.room_design ?? 'design-1',
            sound_enabled: settings.sound_enabled ?? false,
            sound_signal: settings.sound_signal ?? 'signal-1',
            highlight_playable_cards: settings.highlight_playable_cards ?? true,
            card_ordering: settings.card_ordering ?? true,
            left_to_right: settings.left_to_right ?? true,
            manual_ordering: settings.manual_ordering ?? false,
        };
    };

    loadData = async () => {
        await Promise.all([this.loadOptions(), this.loadSettings()]);
        this.setState((old) => ({ ...old, loading: false, formKey: old.formKey + 1 }));
    };

    loadOptions = async () => {
        try {
            const cardRes = await this.app.fn.api.get(`settings/card-styles`);
            if (cardRes.ok && Array.isArray(cardRes.body)) {
                this.setState({ cardStyles: cardRes.body });
            }

            const tableRes = await this.app.fn.api.get(`settings/table-styles`);
            if (tableRes.ok && tableRes.body) {
                this.setState({
                    tableStyles: tableRes.body.table ?? this.state.tableStyles,
                    roomStyles: tableRes.body.room ?? this.state.roomStyles,
                });
            }
        } catch (ex) {
            console.error(ex);
        }
    };

    loadSettings = async () => {
        try {
            const res = await this.app.fn.api.get(`settings`);
            if (res.ok && res.body) {
                this.setState({ values: { ...this.getDefaultsFromApp(), ...res.body } });
                this.app.setState((old) => ({
                    ...old,
                    user: {
                        ...old.user,
                        settings: { ...old.user.settings, ...res.body },
                    },
                }));
            } else {
                this.setState({ values: this.getDefaultsFromApp() });
            }
        } catch (ex) {
            console.error(ex);
            this.setState({ values: this.getDefaultsFromApp() });
        }
    };

    cardBackPreview = () => {
        const { card_back } = this.state.values;
        const style = this.cardBackStyles[card_back] ?? this.cardBackStyles.default;

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                    style={{
                        width: 90,
                        height: 130,
                        borderRadius: 12,
                        border: '1px solid #ccc',
                        boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                        background: style.background,
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div style={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        right: 10,
                        bottom: 10,
                        borderRadius: 10,
                        border: `2px solid ${style.border}`,
                        background: style.inner,
                    }} />
                </div>
                <Text type="secondary">Priekšskatījums</Text>
            </div>
        );
    };

    cardBackStyles = {
        'design-1': { background: 'linear-gradient(135deg, #111 0%, #444 100%)', border: '#CC9F01', inner: 'rgba(255,255,255,0.04)' },
        'design-2': { background: 'linear-gradient(135deg, #8a1111 0%, #d62828 100%)', border: '#ffd166', inner: 'rgba(255,255,255,0.08)' },
        'design-3': { background: 'linear-gradient(135deg, #0b486b 0%, #3498db 100%)', border: '#e0fbfc', inner: 'rgba(255,255,255,0.08)' },
        default:   { background: 'linear-gradient(135deg, #222 0%, #555 100%)', border: '#CC9F01', inner: 'rgba(255,255,255,0.04)' },
    };

    onValuesChange = (_, allValues) => {
        this.setState({ values: { ...this.state.values, ...allValues } });
    };

    handleSubmit = async (values) => {
        this.setState({ saving: true });
        try {
            const payload = {
                ...values,
                sound_signal: values.sound_signal ?? this.state.values.sound_signal,
            };

            const res = await this.app.fn.api.post(`settings`, payload);
            if (res.ok) {
                message.success(`Iestatījumi saglabāti`);
                this.app.setState((old) => ({
                    ...old,
                    user: {
                        ...old.user,
                        settings: { ...old.user.settings, ...res.body },
                    },
                }));
                this.setState({ values: { ...this.state.values, ...res.body } });
            } else {
                message.error(`Neizdevās saglabāt iestatījumus`);
            }
        } catch (ex) {
            console.error(ex);
            message.error(`Neizdevās saglabāt iestatījumus`);
        } finally {
            this.setState({ saving: false });
        }
    };

    content() {
        if (this.state.loading) {
            return (
                <div style={{ width: '100%', padding: 40, textAlign: 'center' }}>
                    <Spin />
                </div>
            );
        }

        return (
            <div style={{ maxWidth: 900, margin: '0 auto' }}>
                <Title level={2} style={{ marginBottom: 24 }}>Iestatījumi</Title>
                <Form
                    key={this.state.formKey}
                    layout="vertical"
                    initialValues={this.state.values}
                    onValuesChange={this.onValuesChange}
                    onFinish={this.handleSubmit}
                >
                    <Card style={{ marginBottom: 20 }}>
                        <Title level={4} style={{ marginTop: 0 }}>Vizuālie iestatījumi</Title>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Form.Item label="Kāršu aizmugure" name="card_back" rules={[{ required: true }]}>
                                    <Select placeholder="Izvēlies dizainu">
                                        {this.state.cardStyles.map(style => (
                                            <Option key={style.value} value={style.value}>{style.label}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                {this.cardBackPreview()}
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="Galda dizains" name="table_design" rules={[{ required: true }]}>
                                    <Select placeholder="Izvēlies dizainu">
                                        {this.state.tableStyles.map(style => (
                                            <Option key={style.value} value={style.value}>{style.label}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="Istabas dizains" name="room_design" rules={[{ required: true }]}>
                                    <Select placeholder="Izvēlies dizainu">
                                        {this.state.roomStyles.map(style => (
                                            <Option key={style.value} value={style.value}>{style.label}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Card style={{ marginBottom: 20 }}>
                        <Title level={4} style={{ marginTop: 0 }}>Skaņas iestatījumi</Title>
                        <Form.Item name="sound_enabled" valuePropName="checked">
                            <Checkbox>Skaņas signāls</Checkbox>
                        </Form.Item>
                        <Form.Item label="Signāla tips" name="sound_signal">
                            <Select disabled={!this.state.values.sound_enabled}>
                                {this.state.soundSignals.map(signal => (
                                    <Option key={signal.value} value={signal.value}>{signal.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Card>

                    <Card style={{ marginBottom: 20 }}>
                        <Title level={4} style={{ marginTop: 0 }}>Spēles iestatījumi</Title>
                        <Form.Item name="highlight_playable_cards" valuePropName="checked">
                            <Checkbox>Izcelt spēlējamās kārtis</Checkbox>
                        </Form.Item>

                        <Divider />

                        <Form.Item name="card_ordering" valuePropName="checked">
                            <Checkbox>Kāršu kārtošana</Checkbox>
                        </Form.Item>

                        {this.state.values.card_ordering && (
                            <div style={{ marginLeft: 24 }}>
                                <Form.Item name="left_to_right" valuePropName="checked">
                                    <Checkbox>Uz labo pusi</Checkbox>
                                </Form.Item>
                                <Form.Item name="manual_ordering" valuePropName="checked">
                                    <Checkbox>Manuāla kārtošana</Checkbox>
                                </Form.Item>
                            </div>
                        )}
                    </Card>

                    <div style={{ textAlign: 'right' }}>
                        <Button type="primary" htmlType="submit" loading={this.state.saving}>
                            Saglabāt
                        </Button>
                    </div>
                </Form>
            </div>
        );
    }
}

export default SettingsWrapper;
